import {
  Badge,
  Button,
  FormField,
  Icon,
  Input,
  List,
  ListRow,
  Section,
  SegmentedControl,
  Skeleton,
  Text,
  useToast,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { PageActions } from '../../../app/PageActions';
import { useRowMenu } from '../../../app/rowActions';
import { errorMessage, formErrors } from '../../../lib/errors';
import { useSession } from '../../identity/session';
import {
  useFinanceCategories,
  useSaveFinanceCategory,
  type EntryKind,
  type FinanceCategory,
} from '../api';
import styles from './FinancePages.module.css';

/** Preset and own categories of income and expenses (FIN-003). */
export function FinanceCategoriesPage() {
  const { t } = useTranslation();
  const rowMenu = useRowMenu();
  const { can } = useSession();
  const categories = useFinanceCategories();
  const save = useSaveFinanceCategory();
  const toast = useToast();
  const [editing, setEditing] = useState<FinanceCategory | 'new' | null>(null);
  const write = can('finance.write');

  // Archiving is reversible, so it happens at once with an undo (§6.10).
  const setArchived = (c: FinanceCategory, archived: boolean) =>
    save.mutate(
      { id: c.id, kind: c.kind, name: c.name, archived, version: c.version },
      {
        onSuccess: (saved) =>
          toast.show(
            archived ? t('finance.categories.archivedToast') : t('finance.categories.restored'),
            'success',
            archived
              ? {
                  label: t('common.undo'),
                  onClick: () =>
                    save.mutate({
                      id: saved.id,
                      kind: saved.kind,
                      name: saved.name,
                      archived: false,
                      version: saved.version,
                    }),
                }
              : undefined,
          ),
        onError: (err) => toast.show(errorMessage(err), 'danger'),
      },
    );

  return (
    <div className={styles.page}>
      {write && (
        <PageActions>
          <Button iconStart={<Icon name="plus" size="sm" />} onClick={() => setEditing('new')}>
            {t('finance.categories.new')}
          </Button>
        </PageActions>
      )}
      {categories.error && (
        <Text tone="danger" role="alert">
          {errorMessage(categories.error)}
        </Text>
      )}
      {categories.isPending && (
        <div role="status" aria-label={t('common.loading')}>
          <Skeleton lines={6} />
        </div>
      )}
      <div className={styles.columns}>
        {(['expense', 'income'] as const).map((kind) => {
          const rows = (categories.data ?? []).filter((c) => c.kind === kind);
          if (rows.length === 0) return null;
          return (
            <Section key={kind} title={t(`finance.categories.title.${kind}`)}>
              <List>
                {rows.map((c) => (
                  <ListRow
                    key={c.id}
                    title={
                      <>
                        {c.name}
                        {c.archived && <Badge size="sm">{t('finance.categories.archived')}</Badge>}
                      </>
                    }
                    description={[
                      c.key ? t('finance.categories.preset') : null,
                      t('finance.categories.entries', { count: c.entries }),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    actionsLabel={rowMenu.label(c.name)}
                    actions={
                      write
                        ? [
                            rowMenu.edit(() => setEditing(c)),
                            {
                              key: 'archive',
                              label: c.archived
                                ? t('finance.categories.restore')
                                : t('finance.categories.archive'),
                              onSelect: () => setArchived(c, !c.archived),
                            },
                          ]
                        : []
                    }
                  />
                ))}
              </List>
            </Section>
          );
        })}
      </div>
      {editing && (
        <CategoryDialog
          {...(editing === 'new' ? {} : { category: editing })}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/** Creates a category (with its kind) or renames one. */
function CategoryDialog({
  category,
  onClose,
}: {
  category?: FinanceCategory;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const save = useSaveFinanceCategory();
  const [kind, setKind] = useState<EntryKind>(category?.kind ?? 'expense');
  const [name, setName] = useState(category?.name ?? '');
  const errors = formErrors(save.error, { name: ['category.'] });
  return (
    <FormDialog
      open
      onClose={onClose}
      title={category ? t('finance.categories.renameTitle') : t('finance.categories.new')}
      submitLabel={category ? t('common.save') : t('finance.categories.create')}
      pending={save.isPending}
      error={errors.general}
      onSubmit={() =>
        save.mutate(
          {
            id: category?.id,
            kind,
            name,
            archived: category?.archived,
            version: category?.version,
          },
          {
            onSuccess: () => {
              toast.show(t('common.saved'));
              onClose();
            },
          },
        )
      }
    >
      {!category && (
        <SegmentedControl<EntryKind>
          label={t('finance.categories.kind')}
          value={kind}
          onChange={setKind}
          fullWidth
          options={[
            { value: 'expense', label: t('finance.kind.expense') },
            { value: 'income', label: t('finance.kind.income') },
          ]}
        />
      )}
      <FormField label={t('finance.categories.name')} required error={errors.field.name}>
        {(p) => (
          <Input {...p} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
        )}
      </FormField>
    </FormDialog>
  );
}
